// NUMA's "get me to green" planner. Pure: takes the river model, the user's
// green bands and today's flow/charge, and returns the concrete moves that
// bring LOAD under the green line, plus what to do about FLOW and CHARGE.
// Nothing here talks to the database — NumaTool applies the moves through
// KYRS's own task columns (scheduled / tabled) and refreshes.
//
// The levers come straight from the weight engine (flo-weights.ts):
//   overdue ×1.25 → rescheduled ×0.35   (give it a real day)
//   unprocessed ×1 → scheduled ×0.35     (process intake: pick a day)
//   anything → paused ×0.1               (table it; KYRS keeps it, the river drops it)
import type { RiverItem, RiverModel } from './flo-weights';
import { stateModifier } from './flo-weights';

export type PlanMove =
  | { kind: 'schedule'; item: RiverItem; to: string; dayLabel: string; weightNow: number; weightAfter: number }
  | { kind: 'table'; item: RiverItem; weightNow: number; weightAfter: number };

export interface GreenPlan {
  /** LOAD moves, in order. Applying them all takes load to `loadAfter`. */
  moves: PlanMove[];
  loadNow: number;
  loadTarget: number;
  loadAfter: number;
  loadGreenNow: boolean;
  /** The lightest open scheduled/overdue items — the quickest flow wins. */
  flowQuickWins: RiverItem[];
  flowGreenNow: boolean;
  chargeGreenNow: boolean;
  /** One line each for the two dials that aren't fixed by moving tasks. */
  flowAdvice: string | null;
  chargeAdvice: string | null;
  allGreen: boolean;
}

export interface GreenBandsLike {
  chargeMin: number;
  loadMaxOfBaseline: number;
  flowMin: number;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

function weightAs(item: RiverItem, state: 'scheduled' | 'paused'): number {
  const cur = stateModifier(item.state) || 1;
  return (item.weight / cur) * stateModifier(state);
}

export function buildGreenPlan(
  model: RiverModel,
  bands: GreenBandsLike,
  flowScore: number,
  charge: number | null,
  now: Date = new Date()
): GreenPlan {
  const loadNow = model.totalLoad;
  const loadTarget = model.baseline * bands.loadMaxOfBaseline;
  const loadGreenNow = loadNow <= loadTarget;
  const flowGreenNow = flowScore >= bands.flowMin;
  const chargeGreenNow = charge == null ? true : charge >= bands.chargeMin;

  const moves: PlanMove[] = [];
  let load = loadNow;
  const taken = new Set<string>();
  const open = model.items.filter((i) => !i.isStone && i.state !== 'excluded');

  if (!loadGreenNow) {
    // 1) Overdue → give each a real day this week (tomorrow onward, spread).
    const overdue = open.filter((i) => i.state === 'overdue').sort((a, b) => b.weight - a.weight);
    let slot = 1;
    for (const item of overdue) {
      if (load <= loadTarget) break;
      const d = new Date(now);
      d.setDate(d.getDate() + slot);
      slot = (slot % 6) + 1;
      const after = weightAs(item, 'scheduled');
      moves.push({ kind: 'schedule', item, to: ymd(d), dayLabel: dayLabel(d), weightNow: item.weight, weightAfter: after });
      load -= item.weight - after;
      taken.add(item.id);
    }
    // 2) Intake → process: pick a day across the week.
    const intake = open.filter((i) => i.state === 'unprocessed').sort((a, b) => b.weight - a.weight);
    for (const item of intake) {
      if (load <= loadTarget) break;
      const d = new Date(now);
      d.setDate(d.getDate() + slot);
      slot = (slot % 6) + 1;
      const after = weightAs(item, 'scheduled');
      moves.push({ kind: 'schedule', item, to: ymd(d), dayLabel: dayLabel(d), weightNow: item.weight, weightAfter: after });
      load -= item.weight - after;
      taken.add(item.id);
    }
    // 3) Still over: table the heaviest of what's left (KYRS keeps them).
    const rest = open.filter((i) => !taken.has(i.id) && i.state !== 'paused').sort((a, b) => b.weight - a.weight);
    for (const item of rest) {
      if (load <= loadTarget) break;
      const after = weightAs(item, 'paused');
      moves.push({ kind: 'table', item, weightNow: item.weight, weightAfter: after });
      load -= item.weight - after;
      taken.add(item.id);
    }
  }

  const flowQuickWins = open
    .filter((i) => (i.state === 'scheduled' || i.state === 'overdue') && !taken.has(i.id))
    .sort((a, b) => a.weight - b.weight)
    .slice(0, 3);

  const flowAdvice = flowGreenNow
    ? null
    : flowQuickWins.length > 0
      ? `Flow is ${Math.round(flowScore)}, green is ${bands.flowMin}. Flow rises when things actually close: finish the ${flowQuickWins.length} lightest ones today and add nothing new.`
      : `Flow is ${Math.round(flowScore)}, green is ${bands.flowMin}. Nothing is scheduled to close — process intake first, then close small things daily.`;

  const chargeAdvice =
    charge == null || chargeGreenNow
      ? null
      : `Charge is ${charge.toFixed(1)}, green is ${bands.chargeMin}. Take a 15-minute break (+0.25), stop switching between focused and complex work, and let the load moves above cut the background drain.`;

  return {
    moves,
    loadNow,
    loadTarget,
    loadAfter: load,
    loadGreenNow,
    flowQuickWins,
    flowGreenNow,
    chargeGreenNow,
    flowAdvice,
    chargeAdvice,
    allGreen: loadGreenNow && flowGreenNow && chargeGreenNow,
  };
}
